/**
 * UI8 Newsletter
 * Pulsing border, pixel indicator input logic, and pill-shaped particle ring
 * for newsletter inputs.
 * Depends on window.PaperShaders and window.PixelIndicator.
 * Usage:
 *   initNewsletterBorder(el);
 *   initNewsletterInput(inputId, placeholderId, statusId, indicatorId);
 *   initNlParticles(canvasId, containerEl);
 */

var _nlPageVisible = true;
document.addEventListener('visibilitychange', function() {
  _nlPageVisible = !document.hidden;
});

function _nlCreateVisibilityPauser(element, onVisible, onHidden) {
  if (!element) return { visible: true };
  var state = { visible: false };
  var obs = new IntersectionObserver(function(entries) {
    state.visible = entries[0].isIntersecting;
    if (entries[0].isIntersecting) { if (onVisible) onVisible(); }
    else { if (onHidden) onHidden(); }
  }, { rootMargin: '100px' });
  obs.observe(element);
  return state;
}

// ── Newsletter pulsing border ─────────────────────────────────────────

function initNewsletterBorder(el) {
  if (!el) return;
  var obs = new IntersectionObserver(function(entries) {
    if (entries[0].isIntersecting) {
      obs.disconnect();
      window.PaperShadersUtils.mountPulsingBorder(el, {
        u_colors: ['#0d91fd', '#1452f0', '#ff2e2e', '#a855f7'],
        u_colorsCount: 4,
        u_roundness: 1.0,
        u_thickness: 0.015,
        u_softness: 1.0,
        u_intensity: 0.1,
        u_bloom: 0.2,
        u_spots: 4,
        u_spotSize: 0.56,
        u_pulse: 0.1,
        u_smoke: 0.3,
        u_smokeSize: 0.0,
      });
    }
  }, { rootMargin: '200px' });
  obs.observe(el);
}

// ── Newsletter input logic ────────────────────────────────────────────


function initNewsletterInput(inputId, placeholderId, statusId, indicatorId) {
  var input = document.getElementById(inputId);
  var placeholder = document.getElementById(placeholderId);
  var status = document.getElementById(statusId);
  var indicator = document.getElementById(indicatorId);
  if (!input || !status) return;

  var pixelCtrl = window.PixelIndicator
    ? window.PixelIndicator.initPixelIndicator(indicator, input, null, { externalControl: true })
    : null;

  var debounceTimer = null;
  var state = 'idle';
  var nlContainer = input.closest('.nl-input');

  function setStatus(msg, type) {
    status.textContent = msg || '';
    status.className = 'nl-input__status' +
      (msg ? ' visible' : '') +
      (type === 'success' ? ' success' : '') +
      (type === 'error' ? ' error' : '');
    nlContainer.classList.toggle('has-status', !!msg);
    indicator.style.cursor = state === 'ready' ? 'pointer' : 'default';
    nlContainer.classList.toggle('ready', state === 'ready');
  }

  function isValidEmail(str) {
    return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,24}$/.test(str);
  }

  input.addEventListener('input', function() {
    if (placeholder) placeholder.style.display = input.value ? 'none' : 'block';
    clearTimeout(debounceTimer);

    if (!input.value) {
      state = 'idle';
      setStatus('');
      if (pixelCtrl) pixelCtrl.setState('idle');
      return;
    }

    state = 'typing';
    setStatus('');
    if (pixelCtrl) { pixelCtrl.setState('typing'); pixelCtrl.spawnRipple(); }

    debounceTimer = setTimeout(function() {
      if (state !== 'typing') return;
      if (isValidEmail(input.value)) {
        state = 'ready';
        setStatus('hit enter to join');
        if (pixelCtrl) pixelCtrl.setState('ready');
      } else {
        state = 'error';
        setStatus('Invalid email', 'error');
        if (pixelCtrl) pixelCtrl.setState('error');
      }
    }, 800);
  });

  input.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') { submit(); return; }
    if (e.key === 'Escape') {
      input.value = '';
      if (placeholder) placeholder.style.display = 'block';
      state = 'idle';
      setStatus('');
      if (pixelCtrl) pixelCtrl.setState('idle');
      clearTimeout(debounceTimer);
      input.blur();
      return;
    }
    if (state === 'typing' && e.key.length === 1 && pixelCtrl) pixelCtrl.spawnRipple();
  });

  function submit() {
    if (state !== 'ready') return;
    state = 'processing';
    setStatus('processing...');
    if (pixelCtrl) pixelCtrl.setState('searching');

    fetch('/api/newsletter/loops', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.value.trim() }),
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      if (data.success) {
        state = 'success';
        setStatus("You're in!", 'success');
        if (pixelCtrl) pixelCtrl.setState('success');
        setTimeout(function() {
          input.value = '';
          if (placeholder) placeholder.style.display = 'block';
          state = 'idle';
          setStatus('');
          if (pixelCtrl) pixelCtrl.setState('idle');
        }, 4000);
      } else if (data.message && data.message.toLowerCase().indexOf('already') !== -1) {
        state = 'error';
        setStatus('Already subscribed', 'error');
        if (pixelCtrl) pixelCtrl.setState('error');
      } else {
        state = 'error';
        setStatus('Something went wrong', 'error');
        if (pixelCtrl) pixelCtrl.setState('error');
      }
    })
    .catch(function() {
      state = 'error';
      setStatus('Something went wrong', 'error');
      if (pixelCtrl) pixelCtrl.setState('error');
    });
  }

  indicator.addEventListener('click', submit);
  indicator.style.cursor = 'default';
}

// ── Newsletter pill particles ─────────────────────────────────────────

function initNlParticles(canvasId, containerEl) {
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
  var canvas = document.getElementById(canvasId);
  var searchEl = containerEl;
  if (!canvas || !searchEl) return;

  var paused = true;
  _nlCreateVisibilityPauser(searchEl, function() { paused = false; }, function() { paused = true; });

  var ctx = canvas.getContext('2d');
  var dpr = Math.min(window.devicePixelRatio, 1.5);
  var bloomCanvas = document.createElement('canvas');
  var bloomCtx = bloomCanvas.getContext('2d');
  var PAD = 80;
  var colors = ['#0df0fd', '#0d91fd', '#ff3a1a', '#ff3a1a', '#ff6200', '#ff3a1a', '#0df0fd', '#ff2e2e'];

  var particles = [], outlinePoints = [];
  var mouseX = -999, mouseY = -999, prevMouseX = -999, prevMouseY = -999, mouseVX = 0, mouseVY = 0;

  function resize() {
    var input = searchEl.querySelector('input');
    var inputRect = input.getBoundingClientRect();
    var searchRect = searchEl.getBoundingClientRect();
    var cw = inputRect.width + PAD * 2, ch = inputRect.height + PAD * 2;
    canvas.width = cw * dpr; canvas.height = ch * dpr;
    canvas.style.width = cw + 'px'; canvas.style.height = ch + 'px';
    canvas.style.left = (inputRect.left - searchRect.left - PAD) + 'px';
    canvas.style.top = (inputRect.top - searchRect.top - PAD) + 'px';
    bloomCanvas.width = Math.ceil(cw * 0.25); bloomCanvas.height = Math.ceil(ch * 0.25);
    genParticles(cw, ch, inputRect.width, inputRect.height);
  }

  function pillPoint(t, straightLen, r, left, top, centerY) {
    var perim = straightLen * 2 + Math.PI * (r * 2);
    var tt = ((t % 1) + 1) % 1 * perim;
    var x, y;
    if (tt < straightLen) {
      x = left + r + tt; y = top;
    } else if (tt < straightLen + Math.PI * r) {
      var a1 = ((tt - straightLen) / r) - Math.PI / 2;
      x = left + r + straightLen + Math.cos(a1) * r; y = centerY + Math.sin(a1) * r;
    } else if (tt < straightLen * 2 + Math.PI * r) {
      x = left + r + straightLen - (tt - straightLen - Math.PI * r); y = top + (r * 2);
    } else {
      var a2 = ((tt - straightLen * 2 - Math.PI * r) / r) + Math.PI / 2;
      x = left + r + Math.cos(a2) * r; y = centerY + Math.sin(a2) * r;
    }
    return { x: x, y: y };
  }

  var PARTICLE_COUNT = 400;
  var OUTLINE_COUNT = 150;

  function genParticles(cw, ch, iw, ih) {
    particles = []; outlinePoints = [];
    var pw = iw + 2, ph = ih + 2, r = ph / 2;
    var left = PAD - 1, top = PAD - 1, cy = top + r;
    var sl = pw - ph;
    for (var i = 0; i < PARTICLE_COUNT; i++) {
      var pt = pillPoint(i / PARTICLE_COUNT, sl, r, left, top, cy);
      pt.x += (Math.random() - 0.5); pt.y += (Math.random() - 0.5);
      particles.push({
        x: pt.x, y: pt.y, homeX: pt.x, homeY: pt.y, vx: 0, vy: 0,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 0.2, sizeVar: Math.random() * 0.15,
        baseAlpha: 0.6 + Math.random() * 0.4,
        scatter: 0.5 + Math.random() * 1.0
      });
    }
    for (var j = 0; j < OUTLINE_COUNT; j++) {
      var opt = pillPoint(j / OUTLINE_COUNT, sl, r, left, top, cy);
      outlinePoints.push({
        x: opt.x, y: opt.y, homeX: opt.x, homeY: opt.y,
        vx: 0, vy: 0, scatter: 0.8 + Math.random() * 0.4
      });
    }
  }

  window.addEventListener('mousemove', function(e) {
    var rect = canvas.getBoundingClientRect();
    prevMouseX = mouseX; prevMouseY = mouseY;
    mouseX = e.clientX - rect.left; mouseY = e.clientY - rect.top;
    mouseVX = mouseX - prevMouseX; mouseVY = mouseY - prevMouseY;
  });

  var enabled = false;
  setTimeout(function() { enabled = true; }, 1000);

  (function tick() {
    requestAnimationFrame(tick);
    if (paused || !_nlPageVisible) return;
    var w = canvas.width / dpr, h = canvas.height / dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, w, h);
    var spd = Math.sqrt(mouseVX * mouseVX + mouseVY * mouseVY);

    var anyActive = false;
    for (var j = 0; j < particles.length; j++) {
      var p = particles[j];
      if (enabled && spd > 0.5) {
        var mx = p.x - mouseX, my = p.y - mouseY, d2 = mx * mx + my * my;
        if (d2 < 625 && d2 > 0) {
          var d = Math.sqrt(d2);
          var pr = 1 - d / 25, ox = mx / d, oy = my / d;
          var f = pr * 0.3 * Math.min(spd, 15) * p.scatter * 0.15;
          p.vx += (mouseVX * 0.25 + ox * 0.75) * f;
          p.vy += (mouseVY * 0.25 + oy * 0.75) * f;
        }
      }
      var dx = p.homeX - p.x, dy = p.homeY - p.y;
      p.vx += dx * 0.006; p.vy += dy * 0.006; p.vx *= 0.93; p.vy *= 0.93;
      p.x += p.vx; p.y += p.vy;
      var disp2 = dx * dx + dy * dy;
      if (disp2 < 0.09) continue;
      anyActive = true;
      var alpha = Math.min(1, Math.sqrt(disp2) / 3) * p.baseAlpha;
      if (alpha < 0.01) continue;
      var s = p.size + p.sizeVar;
      ctx.globalAlpha = alpha; ctx.fillStyle = p.color;
      ctx.fillRect(p.x - s, p.y - s, s * 2, s * 2);
    }
    ctx.globalAlpha = 1;

    outlinePoints.forEach(function(p) {
      if (enabled && spd > 0.5) {
        var mx = p.x - mouseX, my = p.y - mouseY, d = Math.sqrt(mx * mx + my * my);
        if (d < 25 && d > 0) {
          var pr = 1 - d / 25, f = pr * 0.3 * Math.min(spd, 15) * p.scatter;
          p.vx += (mouseVX * 0.25 + (mx / d) * 0.75) * f * 0.15;
          p.vy += (mouseVY * 0.25 + (my / d) * 0.75) * f * 0.15;
        }
      }
      var dx = p.homeX - p.x, dy = p.homeY - p.y;
      p.vx += dx * 0.006; p.vy += dy * 0.006; p.vx *= 0.93; p.vy *= 0.93;
      p.x += p.vx; p.y += p.vy;
    });

    for (var i = 0; i < outlinePoints.length; i++) {
      var p = outlinePoints[i], next = outlinePoints[(i + 1) % outlinePoints.length];
      var d1 = Math.sqrt(Math.pow(p.homeX - p.x, 2) + Math.pow(p.homeY - p.y, 2));
      var d2r = Math.sqrt(Math.pow(next.homeX - next.x, 2) + Math.pow(next.homeY - next.y, 2));
      var sa = Math.min(0.25, Math.max(d1, d2r) / 15);
      if (sa < 0.005) continue;
      ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(next.x, next.y);
      ctx.globalAlpha = sa; ctx.strokeStyle = i % 3 === 0 ? '#2D68F8' : '#AE3349';
      ctx.lineWidth = 1.5; ctx.stroke();
    }
    ctx.globalAlpha = 1;

    if (anyActive) {
      var bw = bloomCanvas.width, bh = bloomCanvas.height;
      bloomCtx.clearRect(0, 0, bw, bh); bloomCtx.drawImage(canvas, 0, 0, bw, bh);
      bloomCtx.filter = 'blur(4px)';
      bloomCtx.drawImage(bloomCanvas, 0, 0);
      bloomCtx.filter = 'none';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.4;
      ctx.drawImage(bloomCanvas, 0, 0, bw, bh, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
    }
    mouseVX *= 0.5; mouseVY *= 0.5;
  })();

  resize();
  var nlResizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(nlResizeTimer);
    nlResizeTimer = setTimeout(resize, 150);
  });
}

window.Newsletter = {
  initNewsletterBorder: initNewsletterBorder,
  initNewsletterInput: initNewsletterInput,
  initNlParticles: initNlParticles,
};
